import { FileText } from "lucide-react";

import { DocumentosLabels } from "@/lib/labels/documentos";
import { cn } from "@/lib/utils";
import type { SignatureTerm } from "@/types/signatures";

export function SendTermsSelection({
	terms,
	selected,
	disabled,
	onToggle,
}: {
	terms: SignatureTerm[] | undefined;
	selected: Set<number>;
	disabled: boolean;
	onToggle: (id: number) => void;
}) {
	return (
		<section data-component="signature-term-selection" className="surface-panel">
			<div className="border-b border-border px-4 py-3">
				<h3 className="text-sm font-semibold text-foreground">
					{DocumentosLabels.preparation.selectedTerms}
				</h3>
				<p className="text-xs text-muted-foreground">{DocumentosLabels.preparation.selectTerm}</p>
			</div>

			{terms !== undefined && terms.length === 0 && (
				<p className="px-4 py-6 text-sm text-muted-foreground">{DocumentosLabels.sendNoTerms}</p>
			)}

			<div className="divide-y divide-border">
				{terms?.map((term) => {
					const checked = selected.has(term.id);

					return (
						<label
							key={term.id}
							data-slot="term-option"
							data-term-id={term.id}
							data-selected={checked}
							className={cn(
								"flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors",
								checked && "bg-primary/[0.04]",
								!term.incomplete &&
									!term.reviewRequired &&
									term.pendingIssues === 0 &&
									"hover:bg-primary/[0.03]",
								(term.incomplete || term.reviewRequired || term.pendingIssues > 0) &&
									"cursor-not-allowed opacity-60",
							)}
						>
							<input
								type="checkbox"
								data-slot="term-checkbox"
								checked={checked}
								disabled={
									disabled || term.incomplete || term.reviewRequired || term.pendingIssues > 0
								}
								onChange={() => onToggle(term.id)}
								className="size-4 accent-primary"
							/>

							<FileText className="size-4 shrink-0 text-muted-foreground" />

							<span className="min-w-0 flex-1 text-sm font-medium text-foreground">
								{term.name}
							</span>

							{(term.incomplete || term.reviewRequired || term.pendingIssues > 0) && (
								<span className="shrink-0 rounded-md bg-warning/10 px-2 py-1 text-xs text-warning">
									{DocumentosLabels.sendReviewRequired}
								</span>
							)}
						</label>
					);
				})}
			</div>
		</section>
	);
}
